/**
 * Utility functions for cleaning and formatting comment text
 */

/**
 * Removes HTML tags and formats comment text for better display
 * @param {string} commentText - Raw comment text that may contain HTML
 * @returns {string} - Clean, formatted comment text
 */
export const cleanAndFormatComment = (commentText) => {
	if (!commentText || typeof commentText !== "string") {
		return "";
	}

	let cleanText = commentText;

	// Remove HTML div tags but preserve their content
	cleanText = cleanText.replace(/<div[^>]*>/gi, "");
	cleanText = cleanText.replace(/<\/div>/gi, "");

	// Remove other HTML tags but preserve their content
	cleanText = cleanText.replace(/<[^>]*>/g, "");

	// Replace HTML entities
	cleanText = cleanText.replace(/&nbsp;/g, " ");
	cleanText = cleanText.replace(/&lt;/g, "<");
	cleanText = cleanText.replace(/&gt;/g, ">");
	cleanText = cleanText.replace(/&amp;/g, "&");
	cleanText = cleanText.replace(/&quot;/g, '"');
	cleanText = cleanText.replace(/&#39;/g, "'");

	// Replace _x000d_ with proper line breaks
	cleanText = cleanText.replace(/_x000d_/g, "\n");

	// Clean up multiple consecutive whitespace/newlines
	cleanText = cleanText.replace(/\n\s*\n/g, "\n"); // Multiple newlines to single
	cleanText = cleanText.replace(/[ \t]+/g, " "); // Multiple spaces/tabs to single space

	// Trim whitespace from start and end
	cleanText = cleanText.trim();

	return cleanText;
};

/**
 * Parses multi-line comments that contain date prefixes and formats them into an array
 * @param {string} commentText - Raw comment text
 * @returns {Array} - Array of parsed comment objects with date, user, and text
 */
export const parseMultiLineComments = (commentText) => {
	if (!commentText || typeof commentText !== "string") {
		return [];
	}

	const cleanText = cleanAndFormatComment(commentText);

	// Split by lines and filter out empty lines
	const lines = cleanText.split("\n").filter((line) => line.trim().length > 0);

	const parsedComments = [];
	lines.forEach((line) => {
		// Match pattern: DD.MM.YYYY HH:MM Name: comment text or DD.MM.YYYY Name: comment text
		const dateTimeUserPattern =
			/^(\d{2}\.\d{2}\.\d{4}(?:\s+\d{2}:\d{2})?)\s+([^:]+):\s*(.*)$/;
		const match = line.match(dateTimeUserPattern);

		if (match) {
			const [, datetime, user, text] = match;
			parsedComments.push({
				date: datetime.trim(),
				user: user.trim(),
				text: text.trim(),
			});
		} else {
			// If no date/user pattern found, treat as continuation of previous comment
			// or as a standalone comment
			if (parsedComments.length > 0) {
				// Append to the last comment's text
				const lastComment = parsedComments[parsedComments.length - 1];
				lastComment.text += " " + line.trim();
			} else {
				// Standalone comment without date/user pattern
				parsedComments.push({
					date: "",
					user: "",
					text: line.trim(),
				});
			}
		}
	});

	return parsedComments;
};

/**
 * Formats a single comment for display, handling both single-line and multi-line formats
 * @param {string} commentText - Raw comment text
 * @returns {object} - Formatted comment display object
 */
export const formatCommentForDisplay = (commentText) => {
	if (!commentText || typeof commentText !== "string") {
		return {
			isMultiLine: false,
			cleanText: "",
			parsedComments: [],
		};
	}

	const cleanText = cleanAndFormatComment(commentText);
	const parsedComments = parseMultiLineComments(commentText);

	// Determine if this is a multi-line comment with date/user patterns
	const isMultiLine =
		parsedComments.length > 1 ||
		(parsedComments.length === 1 &&
			parsedComments[0].date &&
			parsedComments[0].user);

	return {
		isMultiLine,
		cleanText,
		parsedComments,
	};
};
